import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Send, MessageCircleQuestion } from 'lucide-react';
import CandidatoAvatar from '../../components/CandidatoAvatar';
import { capitalize } from './constants';
import { useVereadorChat } from './hooks';
import { VereadorLine, PlayerLine, VereadorTyping } from './ChatBubbles';

function StatBar({ label, value, suffix, color }) {
  const pct = suffix === '%' ? Math.min(100, Math.max(0, value)) : Math.min(100, Math.max(0, (Math.max(0, value) / 500) * 100));
  return (
    <div className="gabinete-dossie-card__bar-row">
      <span className="gabinete-dossie-card__bar-label">{label}</span>
      <div className="gabinete-dossie-card__bar-track">
        <div className={`gabinete-dossie-card__bar-fill ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="gabinete-dossie-card__bar-value">{value}{suffix}</span>
    </div>
  );
}

export default function GabineteScreen({ vereador, playerAvatar, cityName, player }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const scrollRef = useRef(null);
  const { messages, error, ask } = useVereadorChat(vereador, cityName);

  useEffect(() => {
    if (chatOpen) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, chatOpen]);

  if (!vereador) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    ask(question);
    setQuestion('');
  };

  if (chatOpen) {
    return (
      <div className="gabinete-chat">
        <div className="gabinete-chat__header">
          <button onClick={() => setChatOpen(false)} className="gabinete-chat__back">
            <ArrowLeft size={14} /> Voltar ao gabinete
          </button>
          <span className="gabinete-chat__title">
            <MessageCircleQuestion size={14} className="text-[var(--accent)]" /> Falar com {capitalize(vereador.nome)}
          </span>
        </div>

        <div ref={scrollRef} className="gabinete-chat__thread" style={{ scrollbarWidth: 'thin' }}>
          {messages.length === 0 && (
            <VereadorLine vereador={vereador}>
              Precisa de alguma orientação, assessor(a)? Pode perguntar.
            </VereadorLine>
          )}
          {messages.map((m, i) => (
            <React.Fragment key={i}>
              <PlayerLine playerAvatar={playerAvatar}>{m.question}</PlayerLine>
              {m.loading ? <VereadorTyping vereador={vereador} /> : <VereadorLine vereador={vereador}>{m.answer}</VereadorLine>}
            </React.Fragment>
          ))}
        </div>

        <div className="gabinete-chat__footer">
          {error && <p className="text-[11px] text-red-400 mb-2">{error}</p>}
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={`Pergunte algo a ${capitalize(vereador.nome).split(' ')[0]}...`}
              maxLength={300}
              className="flex-1 py-2.5 px-4 rounded-xl text-sm bg-[#0f1a30] border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-white/30"
            />
            <button
              type="submit"
              disabled={!question.trim() || messages.some((m) => m.loading)}
              className="p-2.5 rounded-xl disabled:opacity-40 transition-opacity"
              style={{ background: 'rgba(212,165,116,.95)', color: '#111' }}
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    );
  }

  const ocupacaoValida = vereador.ocupacao && vereador.ocupacao !== '#NULO';

  return (
    <div className="gabinete-room" style={{ scrollbarWidth: 'thin' }}>
      <div className="gabinete-room__wall">
        <div className="gabinete-room__window">
          <p className="gabinete-room__window-city">{cityName}</p>
        </div>
        <p className="gabinete-room__plaque">Gabinete de {capitalize(vereador.nome)} · {vereador.partido}</p>
      </div>

      <div className="gabinete-room__floor">
        <button className="gabinete-room__desk gabinete-room__desk--vereador" onClick={() => setChatOpen(true)}>
          <CandidatoAvatar candidato={vereador} foto={vereador.foto} nome={vereador.nome} size={56} className="rounded-xl border-2 border-white/10" />
          <p className="gabinete-room__desk-name">{capitalize(vereador.nome)}</p>
          <p className="gabinete-room__desk-hint">Clique para conversar</p>
        </button>

        {playerAvatar && (
          <div className="gabinete-room__desk gabinete-room__desk--player">
            <div className="gabinete-room__player-avatar" dangerouslySetInnerHTML={{ __html: playerAvatar.svg }} />
            <p className="gabinete-room__desk-name">{playerAvatar.alias}</p>
            <p className="gabinete-room__desk-hint">Seu posto</p>
          </div>
        )}
      </div>

      {/* Dossiê: detailed profiles for the vereador and, if playing as their
          assessor, the player's own standing too */}
      <div className="gabinete-dossie">
        <div className="gabinete-dossie-card">
          <div className="gabinete-dossie-card__header">
            <CandidatoAvatar candidato={vereador} foto={vereador.foto} nome={vereador.nome} size={44} className="rounded-lg border border-white/10 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="gabinete-dossie-card__name">{capitalize(vereador.nomeCompleto || vereador.nome)}</p>
              <p className="gabinete-dossie-card__role">Vereador(a) · {vereador.partido}</p>
            </div>
          </div>

          <div className="gabinete-dossie-card__meta">
            {vereador.numero && <span>Nº {vereador.numero}</span>}
            {vereador.votos > 0 && <span>{vereador.votos.toLocaleString('pt-BR')} votos</span>}
            {vereador.position && <span>{vereador.position}º mais votado</span>}
          </div>

          {ocupacaoValida && (
            <p className="gabinete-dossie-card__occupation">{capitalize(vereador.ocupacao)}</p>
          )}

          {vereador.indicadores ? (
            <div className="gabinete-dossie-card__bars">
              <StatBar label="Carisma" value={Math.round(vereador.indicadores.carisma)} suffix="%" color="bg-green-400" />
              <StatBar label="Influência" value={Math.round(vereador.indicadores.influencia)} suffix="%" color="bg-purple-400" />
              <StatBar label="Recursos" value={Math.round(vereador.indicadores.recursos)} suffix="%" color="bg-amber-400" />
              <StatBar label="Risco" value={Math.round(vereador.indicadores.risco)} suffix="%" color="bg-red-400" />
            </div>
          ) : (
            <p className="gabinete-dossie-card__empty">Indicadores não disponíveis</p>
          )}
        </div>

        {playerAvatar && player && (
          <div className="gabinete-dossie-card">
            <div className="gabinete-dossie-card__header">
              <div className="gabinete-room__player-avatar shrink-0" dangerouslySetInnerHTML={{ __html: playerAvatar.svg }} />
              <div className="min-w-0 flex-1">
                <p className="gabinete-dossie-card__name">{playerAvatar.alias}</p>
                <p className="gabinete-dossie-card__role">Assessor(a) de {capitalize(vereador.nome)}</p>
              </div>
            </div>

            <div className="gabinete-dossie-card__bars">
              <StatBar label="Carisma" value={player.popularity} suffix="%" color="bg-green-400" />
              <StatBar label="Influência" value={player.influence} suffix="%" color="bg-purple-400" />
              <StatBar label="Recursos" value={Math.max(0, player.money)} suffix="K" color="bg-amber-400" />
              <StatBar label="Risco" value={player.risk} suffix="%" color="bg-red-400" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
